import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// --- Types (mirror backend src/modules/financials/financials.cashflow.ts and
// --- src/routes/scheduledPayments.ts) ----------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

export type CashflowHorizon = 1 | 2 | 3 | 6;

export interface CashflowItem {
  id: string;
  source: 'subscription' | 'planned' | 'invoice';
  direction: 'in' | 'out';
  name: string;
  date: string;
  amount: number;
  currency: string;
  isOverdue?: boolean;
  refId?: string;
}

export interface CurrencyTotal {
  currency: string;
  inflow: number;
  outflow: number;
  net: number;
}

export interface CashflowBucket {
  /** Calendar month, `YYYY-MM`. */
  month: string;
  byCurrency: CurrencyTotal[];
  items: CashflowItem[];
  /** End-of-month estimated balance per currency; null = no known bank balance. */
  projectedBalance: ProjectedBalance[];
}

export interface ProjectedBalance {
  currency: string;
  balance: number | null;
}

/** Current balance of one bank account ("money in accounts" card). */
export interface AccountBalance {
  id: string;
  bankName: string;
  label: string | null;
  iban: string;
  currency: string;
  balance: number | null;
  asOf: string | null;
  source: 'statement' | 'manual' | null;
  manualBalance: number | null;
  manualBalanceAt: string | null;
}

export interface BankBalances {
  accounts: AccountBalance[];
  totals: { currency: string; balance: number; asOf: string | null }[];
}

export interface CashflowProjection {
  months: CashflowHorizon;
  startMonth: string;
  buckets: CashflowBucket[];
  totalsByCurrency: CurrencyTotal[];
  balances: BankBalances;
  excluded: {
    quarterlyAnnualSubscriptions: number;
    usageSubscriptions: number;
  };
}

export type ScheduledPaymentDirection = 'PAYMENT' | 'RECEIPT';
export type ScheduledPaymentStatus = 'PLANNED' | 'DONE' | 'CANCELLED';

export interface ScheduledPayment {
  id: string;
  direction: ScheduledPaymentDirection;
  name: string;
  amount: number;
  currency: string;
  dueDate: string;
  notes: string | null;
  status: ScheduledPaymentStatus;
  companyId: string | null;
  company: { id: string; name: string } | null;
  supplierId: string | null;
  supplier: { id: string; name: string } | null;
  clientId: string | null;
  client: { id: string; name: string } | null;
  projectId: string | null;
  project: { id: string; name: string; code: string | null } | null;
  expenseCategoryId: string | null;
  expenseCategory: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledPaymentListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ScheduledPaymentListResponse {
  success: true;
  data: ScheduledPayment[];
  meta: ScheduledPaymentListMeta;
}

export interface ScheduledPaymentFilters {
  direction?: ScheduledPaymentDirection;
  status?: ScheduledPaymentStatus | 'all';
  from?: string;
  to?: string;
  companyIds?: string[];
  page?: number;
  limit?: number;
}

export interface ScheduledPaymentInput {
  direction: ScheduledPaymentDirection;
  name: string;
  amount: number;
  currency?: string;
  /** ISO datetime or bare YYYY-MM-DD. */
  dueDate: string;
  notes?: string | null;
  status?: ScheduledPaymentStatus;
  companyId?: string | null;
  supplierId?: string | null;
  clientId?: string | null;
  projectId?: string | null;
  expenseCategoryId?: string | null;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** CSV form for the `?companyIds=` query param; undefined when empty (= all firms). */
function companyIdsCsv(companyIds?: string[]): string | undefined {
  return companyIds && companyIds.length > 0 ? companyIds.join(',') : undefined;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getCashflowProjection(
  months: CashflowHorizon,
  companyIds?: string[],
): Promise<CashflowProjection> {
  const query = buildQuery({ months, companyIds: companyIdsCsv(companyIds) });
  const res = await apiFetch<Envelope<CashflowProjection>>(`/api/financials/cashflow${query}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function listScheduledPayments(
  filters: ScheduledPaymentFilters = {},
): Promise<ScheduledPaymentListResponse> {
  const query = buildQuery({
    direction: filters.direction,
    status: filters.status,
    from: filters.from,
    to: filters.to,
    companyIds: companyIdsCsv(filters.companyIds),
    page: filters.page,
    limit: filters.limit,
  });
  return apiFetch<ScheduledPaymentListResponse>(`/api/scheduled-payments${query}`, {
    headers: authHeaders(),
  });
}

export async function createScheduledPayment(
  input: ScheduledPaymentInput,
): Promise<ScheduledPayment> {
  const res = await apiFetch<Envelope<ScheduledPayment>>('/api/scheduled-payments', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateScheduledPayment(
  id: string,
  input: Partial<ScheduledPaymentInput>,
): Promise<ScheduledPayment> {
  const res = await apiFetch<Envelope<ScheduledPayment>>(`/api/scheduled-payments/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteScheduledPayment(id: string): Promise<ScheduledPayment> {
  const res = await apiFetch<Envelope<ScheduledPayment>>(`/api/scheduled-payments/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}
